import { ChevronRight, Code, Grain, HelpOutline, Memory, People, Person, SyncAlt, TapAndPlay } from "@mui/icons-material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {
  Box,
  darken,
  Divider,
  Grid2 as Grid,
  IconButton,
  lighten,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Theme,
  Toolbar,
  Tooltip,
  useTheme
} from "@mui/material";
import classNames from "classnames";
import { useMobile, useWidth } from "../hooks/useWidth";
import { makeStyles } from "@mui/styles";
import BindaptIcon from "../products/Bindapt/BindaptIcon";
import { useLocation, useNavigate } from "react-router-dom";
import BinsIcon from "products/Bindapt/BinsIcon";
import { useGlobalState } from "providers";
import { getFeatures } from "services/whiteLabel";
import MiningIcon from "products/ventilation/MiningIcon";
import { useAuthContext } from "providers/authContext";
import FieldsIcon from "products/AgIcons/FieldsIcon";
import PlaneIcon from "products/AviationIcons/PlaneIcon";
import AirportMapIcon from "products/AviationIcons/AirportMapIcon";
import JobsiteIcon from "products/Construction/JobSiteIcon";
import { getThemeType } from "theme";
import { isCrispEnabled, openCrispChat } from "chat/CrispChat";
import ObjectHeaterIcon from "products/Construction/ObjectHeaterIcon";
import TasksIcon from "products/Construction/TasksIcon";
import CableIcon from "products/Bindapt/CableIcon";
import FieldListIcon from "products/AgIcons/FieldList";
import MarketplaceIcon from "products/CommonIcons/marketplaceIcon";
import DataDuckIcon from "products/Bindapt/DataDuckIcon";
import ContractsIcon from "products/CommonIcons/contractIcon";
import JohnDeereIcon from "products/CommonIcons/johnDeereIcon";
import CNHiIcon from "products/CommonIcons/cnhiIcon";
import LibraCartIcon from "products/CommonIcons/libracartIcon";

const drawerWidth = 230;
const closedDrawerWidth = 9.25;

const useStyles = makeStyles((theme: Theme) => ({
  sideMenu: {
    position: "fixed",
    height: "100%",
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    background: theme.palette.background.default
  },
  sideMenuOpened: {
    zIndex: theme.zIndex.drawer + 2,
    width: drawerWidth,
    minWidth: drawerWidth,
    maxWidth: drawerWidth,
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  sideMenuOnClosed: {
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: "hidden",
    width: theme.spacing(0),
    minWidth: theme.spacing(0),
    maxWidth: theme.spacing(0),
    // zIndex: theme.zIndex.drawer,
    // opacity: 0,
    [theme.breakpoints.up("md")]: {
      width: theme.spacing(closedDrawerWidth),
      minWidth: theme.spacing(closedDrawerWidth),
      maxWidth: theme.spacing(closedDrawerWidth),
      opacity: 1
    }
  },
  sideMenuOnClosedMobile: {
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing(0),
  },
  list: {
    paddingTop: 0
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    maxHeight: 40,
    [theme.breakpoints.up("md")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },
    "&:hover": {
      background: theme.palette.mode === "light" ? darken(theme.palette.background.default, 0.1) : lighten(theme.palette.background.default, 0.1)
    }
  }, 
  activeItem:
    theme.palette.mode === "light"
      ? {
          background: darken(theme.palette.background.default, 0.25),
          "&:hover": {
            background: darken(theme.palette.background.default, 0.15)
          }
        }
      : {
          background: lighten(theme.palette.background.default, 0.25),
          "&:hover": {
            background: lighten(theme.palette.background.default, 0.15)
          }
        }
}))

interface Props {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}
  
export default function SideNavigator(props: Props) {
  const { open, onOpen, onClose } = props;
  const { isAuthenticated } = useAuthContext()
  const theme = useTheme();
  const width = useWidth();
  const classes = useStyles();
  const location = useLocation();
  const [{ user }] = useGlobalState()
  const isMobile = useMobile();

  const navigate = useNavigate();

  const getClasses = (page: string) => {
    if (page === "/device") {
      if (location.pathname.startsWith("/device") ||
      location.pathname.startsWith("/group")) {
        return ({
          root: `${classes.listItem} ${classes.activeItem}`
        })
      }
    }
    if (location.pathname.startsWith(page)) {
      return ({
        root: `${classes.listItem} ${classes.activeItem}`
      })
    }
    return ({
      root: `${classes.listItem}`
    })
  }

  const goTo = (url: string) => {
    onClose();
    navigate(url)
  }

  const authenticatedSideMenu = () => {
    const f = getFeatures();
    return (
      <List className={classes.list} component="nav">
        {(f.visualFarm || user.hasFeature("admin")) && (
        <Tooltip title="Visual Farm" placement="right">
          <ListItemButton
            id="tour-visual-farm"
            onClick={() => goTo("/visualFarm")}
            classes={getClasses("/visualFarm")}
            >
            <ListItemIcon>
              <FieldsIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Visual Farm" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.aviationMap || user.hasFeature("admin")) && (
        <Tooltip title="Aviation Map" placement="right">
          <ListItemButton
            id="tour-aviation-map"
            onClick={() => goTo("/aviationMap")}
            classes={getClasses("/aviationMap")}
            >
            <ListItemIcon>
              <AirportMapIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Airport Map" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.constructionMap || user.hasFeature("admin")) && (
        <Tooltip title="Construction Map" placement="right">
          <ListItemButton
            id="tour-construction-map"
            onClick={() => goTo("/constructionMap")}
            classes={getClasses("/constructionMap")}
            >
            <ListItemIcon>
              <FieldsIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Construction Map" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.contracts || user.hasFeature("admin")) && (
          <Tooltip title="Contracts" placement="right">
            <ListItemButton
              id="tour-contracts"
              onClick={() => goTo("/contracts")}
              classes={getClasses("/contracts")}
              >
              <ListItemIcon>
                <ContractsIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Contracts" />}
            </ListItemButton>
          </Tooltip>
        )}
        {(f.bins || user.hasFeature("admin")) && (
        <Tooltip title="Bins" placement="right">
          <ListItemButton
            id="tour-bins"
            onClick={() => goTo("/bins")}
            classes={getClasses("/bin")}
            >
            <ListItemIcon>
              <BinsIcon height={"26px"}/>
            </ListItemIcon>
            {open && <ListItemText primary="Bins" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.terminals || user.hasFeature("admin")) && (
          <Tooltip title="Terminals" placement="right">
            <ListItemButton
              id="tour-terminals"
              onClick={() => goTo("/terminals")}
              classes={getClasses("/terminal")}
              >
              <ListItemIcon>
                <PlaneIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Terminals" />}
            </ListItemButton>
          </Tooltip>
        )}
        {(f.cableEstimator && user.hasFeature("installer") || user.hasFeature("admin")) &&
          <Tooltip title="Cable Estimator" placement="right">
            <ListItemButton
              id="tour-cable-estimator"
              onClick={() => goTo("/cableEstimate")}
              classes={getClasses("/cableEstimate")}
              >
              <ListItemIcon>
                <CableIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Cable Estimator" />}
            </ListItemButton>
          </Tooltip>
        }
        {(f.transactions || user.hasFeature("admin")) && (
          <Tooltip title="Transactions" placement="right">
            <ListItemButton
              id="tour-transactions"
              onClick={() => goTo("/transactions")}
              classes={getClasses("/transactions")}
              >
              <ListItemIcon>
                <SyncAlt />
              </ListItemIcon>
              {open && <ListItemText primary="Transactions" />}
            </ListItemButton>
          </Tooltip>
        )}
        {(f.mines || user.hasFeature("admin")) && (
          <Tooltip title="Mines" placement="right">
            <ListItemButton
              id="tour-mines"
              onClick={() => goTo("/mines")}
              classes={getClasses("/mine")}
              >
              <ListItemIcon>
                <MiningIcon height={"26px"} width={"26px"} />
              </ListItemIcon>
              {open && <ListItemText primary="Mines" />}
            </ListItemButton>
          </Tooltip>
        )}
        <Tooltip title="Devices" placement="right">
          <ListItemButton
            id="tour-dashboard"
            onClick={() => goTo("/devices")}
            classes={getClasses("/device")}
            >
            <ListItemIcon>
              <BindaptIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Devices" />}
          </ListItemButton>
        </Tooltip>
        {user.hasFeature("admin") &&
          <Tooltip title="Firmware" placement="right">
            <ListItemButton
              id="tour-firmware"
              onClick={() => goTo("/firmware")}
              classes={getClasses("/firmware")}
              >
              <ListItemIcon>
                <Memory />
              </ListItemIcon>
              {open && <ListItemText primary="Logs" />}
            </ListItemButton>
          </Tooltip>
        }
        {user.hasFeature("admin") &&
          <Tooltip title="Logs" placement="right">
            <ListItemButton
              id="tour-logs"
              onClick={() => goTo("/logs")}
              classes={getClasses("/logs")}
              >
              <ListItemIcon>
                <DataDuckIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Logs" />}
            </ListItemButton>
          </Tooltip>
        }
        <Tooltip title="Teams" placement="right">
          <ListItemButton
            id="tour-teams"
            onClick={() => goTo("/teams")}
            classes={getClasses("/team")} 
            >
            <ListItemIcon>
              <People style={{ color: getThemeType() === "light" ? "black" : undefined }}/>
            </ListItemIcon>
            {open && <ListItemText primary="Teams" />}
          </ListItemButton>
        </Tooltip>
        <Tooltip title="Users" placement="right">
          <ListItemButton
            id="tour-users"
            onClick={() => goTo("/users")}
            classes={getClasses("/user")}
          >
            <ListItemIcon>
              <Person style={{ color: getThemeType() === "light" ? "black" : undefined }}/>
            </ListItemIcon>
            {open && <ListItemText primary="Users" />}
          </ListItemButton>
        </Tooltip>
        {(f.fields || user.hasFeature("admin")) && (
        <Tooltip title="My Fields" placement="right">
          <ListItemButton
            id="tour-field-list"
            onClick={() => goTo("/fields")}
            classes={getClasses("/fields")}
            >
            <ListItemIcon>
              <FieldListIcon />
            </ListItemIcon>
            {open && <ListItemText primary="My Fields" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.grainTypes || user.hasFeature("admin")) && (
        <Tooltip title="Grain Types" placement="right">
          <ListItemButton
            id="tour-grain-types"
            onClick={() => goTo("/grains")}
            classes={getClasses("/grains")}
            >
            <ListItemIcon>
              <Grain />
            </ListItemIcon>
            {open && <ListItemText primary="Grain Types" />}
          </ListItemButton>
        </Tooltip>
        )}

        {(f.jobsites || user.hasFeature("admin")) && (
        <Tooltip title="Jobsites" placement="right">
          <ListItemButton
            id="tour-jobsites"
            onClick={() => goTo("/jobsites")}
            classes={getClasses("/jobsites")}
            >
            <ListItemIcon>
              <JobsiteIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Jobsites" />}
          </ListItemButton>
        </Tooltip>
        )}
        {(f.heaters || user.hasFeature("admin")) && (
        <Tooltip title="Heaters" placement="right">
          <ListItemButton
            id="tour-heaters"
            onClick={() => goTo("/heaters")}
            classes={getClasses("/heaters")}
            >
            <ListItemIcon>
              <ObjectHeaterIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Heaters" />}
          </ListItemButton>
        </Tooltip>
        )}
        <Tooltip title="Tasks" placement="right">
          <ListItemButton
            id="tour-tasks"
            onClick={() => goTo("/tasks")}
            classes={getClasses("/tasks")}
            >
            <ListItemIcon>
              <TasksIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Tasks" />}
          </ListItemButton>
        </Tooltip>

        {user.hasFeature("developer") &&
          <Tooltip title="API documentation" placement="right">
            <ListItemButton
              id="tour-api-docs"
              onClick={() => goTo("/api")}
              classes={getClasses("/api")}
              >
              <ListItemIcon>
                <Code />
              </ListItemIcon>
              {open && <ListItemText primary="API Documentation" />}
            </ListItemButton>
          </Tooltip>
        }
        {user.hasFeature("admin") && window.NDEFReader &&
          <Tooltip title="NFC" placement="right">
            <ListItemButton
              id="tour-nfc"
              onClick={() => goTo("/nfc")}
              classes={getClasses("/nfc")}
              >
              <ListItemIcon>
                <TapAndPlay />
              </ListItemIcon>
              {open && <ListItemText primary="NFC" />}
            </ListItemButton>
          </Tooltip>
        }
        {(f.marketplace || user.hasFeature("admin")) &&
          <Tooltip title="Marketplace" placement="right">
            <ListItemButton
              id="tour-marketplace"
              onClick={() => goTo("/marketplace")}
              classes={getClasses("/marketplace")}
              >
              <ListItemIcon>
                <MarketplaceIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Marketplace" />}
            </ListItemButton>
          </Tooltip>
        }
        {user.hasFeature("john-deere") &&
          <Tooltip title="John Deere" placement="right">
            <ListItemButton
              id="tour-jd"
              onClick={() => goTo("/johndeere")}
              classes={getClasses("/johndeere")}
              >
              <ListItemIcon>
                <JohnDeereIcon />
              </ListItemIcon>
              {open && <ListItemText primary="John Deere" />}
            </ListItemButton>
          </Tooltip>
        }
        {user.hasFeature("cnhi") &&
          <Tooltip title="Case New Holland" placement="right">
            <ListItemButton
              id="tour-cnhi"
              onClick={() => goTo("/cnhi")}
              classes={getClasses("/cnhi")}
              >
              <ListItemIcon>
                <CNHiIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Case New Holland" />}
            </ListItemButton>
          </Tooltip>
        }
        {user.hasFeature("libra-cart") &&
          <Tooltip title="Libra Cart" placement="right">
            <ListItemButton
              id="tour-libraCart"
              onClick={() => goTo("/libracart")}
              classes={getClasses("/libracart")}
              >
              <ListItemIcon>
                <LibraCartIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Libra Cart" />}
            </ListItemButton>
          </Tooltip>
        } 
      </List>
    )
  }

  return (
    <SwipeableDrawer
      // variant={width === "xs" || width === "sm" ? "temporary" : "permanent"}
      variant={!open ? "permanent" : width === "xs" || width === "sm" ? "temporary" : "permanent"}
      classes={{
        paper: classNames(
          classes.sideMenu,
          open ? classes.sideMenuOpened : (isMobile ? classes.sideMenuOnClosedMobile : classes.sideMenuOnClosed)
        )
      }}
      anchor="left"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      sx={{ pointerEvents: isMobile&&!open ? "none" : "auto"}}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <Toolbar disableGutters sx={{ px: 1 }}>
          <Grid container direction="row" justifyContent={"flex-end"}>
            <Grid>
              <IconButton
                onClick={open ? onClose : onOpen}
                aria-label={open ? "Close side menu" : "Open side menu"}
              >
                {open
                  ? theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />
                  : theme.direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Grid>
          </Grid>
        </Toolbar>
        <Divider />
        <Box sx={{ flex: 1, overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {isAuthenticated && authenticatedSideMenu()}
        </Box>
        {isCrispEnabled() && (<>
          <Divider sx={{ my: 1 }} />
          <List className={classes.list} component="nav">
            <Tooltip title="Help" placement="right">
              <ListItemButton classes={{ root: classes.listItem }} onClick={() => openCrispChat()}>
                <ListItemIcon>
                  <HelpOutline style={{ color: getThemeType() === "light" ? "black" : undefined }} />
                </ListItemIcon>
                {open && <ListItemText primary="Help" />}
              </ListItemButton>
            </Tooltip>
          </List>
        </>)}
      </Box>
    </SwipeableDrawer>
  );
}
